import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Instagram,
  Facebook,
  Linkedin,
  Video,
  Sparkles,
  Copy,
  Check,
  ArrowLeft,
  Image as ImageIcon,
  Clock,
  Hash,
  Loader2,
  Download,
  Wand2,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { PublishPromoModal } from './PublishPromoModal';
import { Rocket } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: 'from-pink-500 to-purple-600' },
  { id: 'facebook', label: 'Facebook', icon: Facebook, color: 'from-blue-500 to-blue-700' },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: 'from-sky-600 to-sky-800' },
  { id: 'reel', label: 'Instagram Reel', icon: Video, color: 'from-fuchsia-500 to-pink-600' },
];

const LANGUAGES = [
  { id: 'pt', label: '🇵🇹 Português' },
  { id: 'en', label: '🇬🇧 English' },
  { id: 'es', label: '🇪🇸 Español' },
  { id: 'fr', label: '🇫🇷 Français' },
];

const TONES = [
  { id: 'warm-premium', label: 'Caloroso & Premium (padrão)' },
  { id: 'inspirational', label: 'Inspiracional / Aspiracional' },
  { id: 'informative', label: 'Informativo & Educacional' },
  { id: 'storytelling', label: 'Storytelling Pessoal' },
  { id: 'urgent-deal', label: 'Urgência / Oferta Especial' },
];

const TOPIC_IDEAS = [
  'Sunset over the Douro Valley vineyards in autumn',
  'Why book a private transfer instead of a taxi at Porto Airport',
  'Day trip from Porto to Santiago de Compostela',
  'Tour privado a Braga e Guimarães em 1 dia',
  'Wine tasting experience at family-owned quintas',
  'How to plan the perfect Douro Valley wine tour',
  'Porto Airport transfer for business travelers',
  'Suspension bridge 516 Arouca — Paiva walkways adventure',
];

const CopyButton = ({ text, label = 'Copiar' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Não foi possível copiar');
    }
  };
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={handleCopy}
      className="gap-1.5"
      data-testid="copy-button"
    >
      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
      {copied ? 'Copiado' : label}
    </Button>
  );
};

const ContentResult = ({ content, platform, language }) => {
  const [generatingImg, setGeneratingImg] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [customImagePrompt, setCustomImagePrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState(platform === 'reel' ? '9:16' : platform === 'linkedin' ? '16:9' : '1:1');
  const [publishOpen, setPublishOpen] = useState(false);

  if (!content) return null;

  const handleGenerateImage = async (promptOverride) => {
    const promptToUse = (promptOverride || customImagePrompt || content.image_idea || '').trim();
    if (promptToUse.length < 5) {
      toast.error('Escreva uma descrição da imagem (mínimo 5 caracteres)');
      return;
    }
    setGeneratingImg(true);
    setGeneratedImage(null);
    try {
      const res = await axios.post(
        `${API}/marketing/generate-image`,
        { prompt: promptToUse, aspect_ratio: aspectRatio },
        { timeout: 120000 }
      );
      setGeneratedImage(res.data.image);
      toast.success('Imagem gerada!');
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      toast.error(`Erro ao gerar imagem: ${msg}`);
    } finally {
      setGeneratingImg(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    const link = document.createElement('a');
    link.href = generatedImage.data_url;
    link.download = `govip-post-${Date.now()}.${generatedImage.mime_type.split('/')[1] || 'jpg'}`;
    link.click();
  };

  if (content.raw) {
    return (
      <Card className="border-orange-200 bg-orange-50">
        <CardContent className="p-4">
          <p className="text-sm text-orange-800 mb-2 font-medium">⚠ Resposta em texto bruto:</p>
          <pre className="whitespace-pre-wrap text-sm text-gray-800">{content.raw}</pre>
          <div className="mt-3"><CopyButton text={content.raw} label="Copiar tudo" /></div>
        </CardContent>
      </Card>
    );
  }

  const hashtagsText = (content.hashtags || []).join(' ');
  const captionPlus = `${content.caption || ''}${hashtagsText ? '\n\n' + hashtagsText : ''}`;

  return (
    <div className="space-y-4">
      {/* Caption */}
      {content.caption && (
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-600" />
              Caption
            </CardTitle>
            <div className="flex gap-2">
              <CopyButton text={content.caption} label="Caption" />
              <CopyButton text={captionPlus} label="Tudo (caption + hashtags)" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="whitespace-pre-wrap text-gray-800 leading-relaxed text-sm bg-gray-50 p-4 rounded-lg border border-gray-200" data-testid="generated-caption">
              {content.caption}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {content.caption.length} caracteres
            </p>
          </CardContent>
        </Card>
      )}

      {/* Reel-specific: hook + scenes */}
      {platform === 'reel' && content.hook && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Video className="w-4 h-4 text-fuchsia-600" />
              Hook (3 primeiros segundos)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-800 font-medium italic bg-fuchsia-50 p-3 rounded border border-fuchsia-200">
              {content.hook}
            </p>
          </CardContent>
        </Card>
      )}

      {platform === 'reel' && Array.isArray(content.scenes) && content.scenes.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Video className="w-4 h-4 text-fuchsia-600" />
              Storyboard ({content.scenes.length} cenas)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {content.scenes.map((scene, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-fuchsia-600 text-white text-xs font-bold px-2 py-0.5 rounded">
                    {scene.timecode || `Cena ${idx + 1}`}
                  </span>
                </div>
                <p className="text-sm text-gray-700"><strong>Visual:</strong> {scene.visual}</p>
                {scene.on_screen_text && <p className="text-sm text-gray-700 mt-1"><strong>Texto na tela:</strong> {scene.on_screen_text}</p>}
                {scene.voiceover && <p className="text-sm text-gray-700 mt-1"><strong>Voz off:</strong> "{scene.voiceover}"</p>}
              </div>
            ))}
            {content.music_vibe && (
              <p className="text-sm text-gray-600 mt-2">
                🎵 <strong>Música:</strong> {content.music_vibe}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Hashtags */}
      {Array.isArray(content.hashtags) && content.hashtags.length > 0 && (
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Hash className="w-4 h-4 text-amber-600" />
              Hashtags ({content.hashtags.length})
            </CardTitle>
            <CopyButton text={hashtagsText} label="Hashtags" />
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5" data-testid="generated-hashtags">
              {content.hashtags.map((tag, i) => (
                <span key={i} className="text-sm bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium">
                  {tag}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* IMAGE GENERATION CARD */}
      <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-amber-600" />
            Gerar Imagem com IA (Nano Banana)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {content.image_idea && (
            <p className="text-sm text-gray-600 italic">
              💡 <strong>Sugestão:</strong> {content.image_idea}
            </p>
          )}
          <Textarea
            value={customImagePrompt}
            onChange={(e) => setCustomImagePrompt(e.target.value)}
            placeholder={content.image_idea || 'Descreva a imagem que quer gerar (EN preferível para melhor qualidade)...'}
            rows={2}
            className="bg-white text-sm"
            data-testid="image-prompt-input"
          />
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1">
              {[
                { v: '1:1', label: '1:1 Feed' },
                { v: '4:5', label: '4:5 Retrato' },
                { v: '9:16', label: '9:16 Reel' },
                { v: '16:9', label: '16:9 LinkedIn' },
              ].map((r) => (
                <button
                  key={r.v}
                  type="button"
                  onClick={() => setAspectRatio(r.v)}
                  className={`text-xs px-2.5 py-1 rounded-full font-medium transition ${
                    aspectRatio === r.v
                      ? 'bg-amber-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-amber-400'
                  }`}
                  data-testid={`ratio-${r.v}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <Button
              type="button"
              size="sm"
              onClick={() => handleGenerateImage()}
              disabled={generatingImg}
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white ml-auto"
              data-testid="generate-image-btn"
            >
              {generatingImg ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> A gerar (20-40s)…</>
              ) : (
                <><Wand2 className="w-4 h-4 mr-1.5" /> Gerar Imagem</>
              )}
            </Button>
          </div>

          {generatedImage && (
            <div className="mt-3 bg-white rounded-lg border border-gray-200 p-3">
              <img
                src={generatedImage.data_url}
                alt="Generated"
                className="w-full max-h-96 object-contain rounded mx-auto"
                data-testid="generated-image"
              />
              <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                <span>{Math.round(generatedImage.size_bytes / 1024)} KB · {aspectRatio}</span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => setPublishOpen(true)}
                    className="bg-gradient-to-r from-amber-600 to-amber-700 text-white hover:from-amber-700 hover:to-amber-800"
                    data-testid="publish-promo-btn"
                  >
                    <Rocket className="mr-1.5 h-4 w-4" />
                    Publicar como Promo
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleDownload} data-testid="download-image-btn">
                    <Download className="w-4 h-4 mr-1.5" />
                    Descarregar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Publish-as-Promo modal */}
      <PublishPromoModal
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        image={generatedImage}
        caption={content?.caption || ''}
        hashtags={content?.hashtags || []}
        platform={platform}
        aspectRatio={aspectRatio}
        sourceLanguage={language}
      />

      {/* Image idea & Best time */}
      <div className="grid md:grid-cols-2 gap-3">
        {content.best_time_to_post && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-600" />
                Melhor altura
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 capitalize">{content.best_time_to_post}</p>
            </CardContent>
          </Card>
        )}
        {platform === 'reel' && (
          <Card className="bg-fuchsia-50 border-fuchsia-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Video className="w-4 h-4 text-fuchsia-600" />
                Dica de filmagem
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-fuchsia-900">
                Use o storyboard acima + filme com o telemóvel em 9:16. Para a thumbnail/capa, use a imagem gerada.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export const AdminMarketing = () => {
  const [platform, setPlatform] = useState('instagram');
  const [language, setLanguage] = useState('pt');
  const [tone, setTone] = useState('warm-premium');
  const [topic, setTopic] = useState('');
  const [extraInstructions, setExtraInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (topic.trim().length < 5) {
      toast.error('Descreva o tema (mínimo 5 caracteres)');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/marketing/generate-post`,
        { platform, topic: topic.trim(), language, tone, extra_instructions: extraInstructions.trim() },
        { timeout: 90000 }
      );
      setResult({ platform: res.data.platform, content: res.data.content });
      toast.success(`Conteúdo gerado para ${platform}!`);
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.detail || error.message;
      toast.error(`Erro ao gerar: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-10 px-4">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <Link
              to="/admin/bookings"
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2"
              data-testid="back-to-bookings"
            >
              <ArrowLeft className="w-4 h-4" /> Reservas
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-amber-500" />
              Marketing AI
            </h1>
            <p className="text-gray-600 mt-1">
              Gerador de conteúdo para redes sociais — Claude Sonnet 4.5 · 4 idiomas
            </p>
          </div>
          <Link
            to="/admin/promos"
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100"
            data-testid="link-admin-promos"
          >
            <Rocket className="h-4 w-4" />
            Gerir Promos do Homepage
          </Link>
        </div>

        {/* Generator form */}
        <Card className="mb-8 shadow-md">
          <CardHeader>
            <CardTitle>Gerar Novo Post</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleGenerate} className="space-y-5">
              {/* Platform selector */}
              <div>
                <Label className="mb-2 block">Plataforma</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {PLATFORMS.map((p) => {
                    const Icon = p.icon;
                    const active = platform === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPlatform(p.id)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                          active
                            ? 'border-amber-500 bg-amber-50 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                        data-testid={`platform-${p.id}`}
                      >
                        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${p.color} flex items-center justify-center`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-sm font-medium text-gray-700">{p.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Language & Tone */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Idioma</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger data-testid="language-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-2 block">Tom</Label>
                  <Select value={tone} onValueChange={setTone}>
                    <SelectTrigger data-testid="tone-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TONES.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Topic */}
              <div>
                <Label className="mb-2 block">
                  Tema do post *
                </Label>
                <Input
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="Ex: Tour ao Douro nas vindimas com prova de vinhos"
                  required
                  minLength={5}
                  data-testid="topic-input"
                />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {TOPIC_IDEAS.slice(0, 4).map((idea, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setTopic(idea)}
                      className="text-xs px-2.5 py-1 bg-gray-100 hover:bg-amber-100 text-gray-700 rounded-full transition"
                    >
                      {idea.length > 50 ? idea.slice(0, 50) + '…' : idea}
                    </button>
                  ))}
                </div>
              </div>

              {/* Extra instructions (collapsed) */}
              <details>
                <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-900">
                  + Instruções extra (opcional)
                </summary>
                <Textarea
                  value={extraInstructions}
                  onChange={(e) => setExtraInstructions(e.target.value)}
                  placeholder="Ex: Mencionar promoção 10% ida e volta. Destacar segurança para famílias com crianças. Etc."
                  rows={3}
                  className="mt-2"
                  data-testid="extra-instructions"
                />
              </details>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800 text-white w-full"
                data-testid="generate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    A gerar conteúdo… (15–30s)
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Gerar Post
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Result */}
        {result && (
          <div data-testid="result-section">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Resultado</h2>
            <ContentResult content={result.content} platform={result.platform} language={language} />
          </div>
        )}
      </div>
    </div>
  );
};
